import { AxiosResponse } from "axios";
import { invert, isEmpty, mapKeys, pick, pickBy, snakeCase } from "lodash";
import moment, { Moment } from "moment";
import {
  SampleAvailabilityIcons,
  SampleAvailabilityMessages
} from "../../enums";
import { NotificationsLevel, TimeEnum, TimeRangeEnum } from "../../lib/enums";
import { areNotificationsShown, show } from "../../notifications/notifications";
import { ErrorResponse, MessageErrorResponse } from "../../requests/@types";
import { CloudSearchSample, LocalSearchSample } from "../../services/@types";
import { enums } from "../../shim/jsbridge";
import { isNtiValue } from "../../utils";
import { removeSurroundingQuotes } from "../network/util";
import {
  CloudSample,
  IValidationError,
  LocalSample,
  Selection,
  Selections
} from "./@types";
import {
  GridDataFieldMapKeyType,
  TimePeriodEnumValue
} from "./@types/EnumTypes";
import { UserSampleCount } from "./@types/UserList";
import { RequestedHashes } from "./@types/validatequery";
import {
  CLOUD_LIMIT_MOMENT,
  commaSeparatedValuesRegex,
  DATE_FIELDS,
  DATE_FILTER,
  endingWithOperatorsRegex,
  FILTER_KEYWORDS,
  FIRST_COLON_ONLY_REGEX,
  gridDataFieldMap,
  isAvailableKeywordRegex,
  isNotAvailableKeywordRegex,
  ISO_FORMAT_WITHOUT_MILLIS,
  ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC,
  ISO_YEAR_MONTH_DAY_FORMAT_UTC,
  LOCATION,
  MAX_NETWORK_VALUE_LENGTH,
  NON_NETWORK_TAB_KEYWORD_VALUE_REGEX,
  NTI_KEYWORD_VALUE_REGEX,
  NTI_KEYWORDS,
  operatorsRegex,
  rangeSeparatedValuesRegex,
  SEARCH_FIELD_TO_FILTER_MAP,
  timestampRegex
} from "./consts";
import { removeBrackets } from "./containers/utils";
import {
  DEFAULT_CLOUD_TIME_SELECTION,
  DEFAULT_LOCAL_TIME_SELECTIONS,
  DEFAULT_NETWORK_TIME_SELECTION
} from "./hooks";

import { hasBalancedBrackets, SEARCH_FIELDS } from "./mappings";
import { parseQuery } from "./components/autosuggest/QueryParser";
import { getLabelFromQuery } from "./components/autosuggest/utils/convertQueryToString";

const {
  analyzer: { TISCALE, URLANALYZER, TISCALE_DROPPED_FILE }
} = enums || {};

const {
  CLASSIFICATION,
  SUBMISSION_TIME,
  TIME_TYPE,
  PERIOD_TYPE,
  LAST_SEEN,
  FIRST_SEEN,
  SUBMISSION_USER,
  PROCESSING_STATUS,
  SAMPLE_ORIGIN,
  CLOUD_SEARCH_TYPE
} = FILTER_KEYWORDS;
const { CLASS, AVAILABLE, SHAREABLE, IN_ALIAS } = SEARCH_FIELDS;

const sortFieldMap = {
  ...invert(gridDataFieldMap),
  file_count: "filecount"
};

export const isAvailableKeywordInQuery = (query = "") =>
  isAvailableKeywordRegex.test(query) || isNotAvailableKeywordRegex.test(query);

export const formatSearchQuery = (query: string) => {
  // Required to find out if the user has entered comma.
  // For the comma the input cursor should be positioned in front of the number. TCB-13767
  let doubleMovedCursor = false;
  let modified = query.replace(commaSeparatedValuesRegex, "$1 $3");

  if (query !== modified) {
    doubleMovedCursor = true;
  }

  modified = modified
    .replace(rangeSeparatedValuesRegex, "$1 TO $3")
    .replace(operatorsRegex, match => match.toUpperCase());

  return { modified, doubleMovedCursor };
};

export const now = moment().utc().format(ISO_FORMAT_WITHOUT_MILLIS);

export const stringToTimestampStart = (
  value: string,
  format = ISO_FORMAT_WITHOUT_MILLIS
) => {
  const today = moment().utc().startOf("day");
  switch (value) {
    case DATE_FILTER.TODAY:
    case TimeRangeEnum.today:
      return `${today.format(format)}`;
    case DATE_FILTER.LAST_WEEK:
    case TimeRangeEnum.lastWeek:
      return `${today.subtract(7, "days").format(format)}`;
    case DATE_FILTER.LAST_MONTH:
    case TimeRangeEnum.lastMonth:
      return `${today.subtract(1, "months").format(format)}`;
    case DATE_FILTER.LAST_QUARTER:
    case TimeRangeEnum.lastQuarter:
      return `${today.subtract(3, "months").format(format)}`;
    case DATE_FILTER.LAST_YEAR:
    case TimeRangeEnum.lastYear:
      return `${today.subtract(1, "year").format(format)}`;
    case DATE_FILTER.ALL:
      return "*";
    default:
      return undefined;
  }
};

export const stringToTimestamp = (value = "") => {
  const start = stringToTimestampStart(value);
  if (!start) return value;
  const end = moment().utc().startOf("day").format(ISO_FORMAT_WITHOUT_MILLIS);
  return `[${start} TO ${end}]`;
};

export const fromTimestampToString = (time: string) => {
  const [endDate] = time.replace(timestampRegex, "").split(" ");
  const formattedDate = moment(endDate).format(
    ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC
  );
  let timeEnum;

  switch (formattedDate) {
    case moment().format(ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC):
      timeEnum = TimeEnum.TODAY;
      break;
    case moment()
      .subtract(1, "week")
      .format(ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC):
      timeEnum = TimeEnum.LAST_WEEK;
      break;
    case moment()
      .subtract(1, "month")
      .format(ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC):
      timeEnum = TimeEnum.LAST_MONTH;
      break;
    case moment()
      .subtract(3, "months")
      .format(ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC):
      timeEnum = TimeEnum.LAST_QUARTER;
      break;
    case moment()
      .subtract(1, "year")
      .format(ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC):
      timeEnum = TimeEnum.LAST_YEAR;
      break;
    default:
      // Date is custom, so we need to return full date
      return time;
  }

  return timeEnum;
};

/**
 * Checks if query is used for querying datetime fields.
 * @param query
 * @returns {boolean}
 */
export const isDateField = (query: string) => DATE_FIELDS.includes(query);

export const formatDate = (date: Moment) =>
  date.toISOString().replace(/\..+$/, "Z");

/**
 * Checks if search query is valid.
 * @param query
 * @returns {boolean} true if query is valid, otherwise false
 */
export const isValidSearchQuery = (query: string) =>
  hasBalancedBrackets(query) && !query.match(endingWithOperatorsRegex);

export const parseJSON = (data: string) => {
  try {
    return JSON.parse(data);
  } catch (ex) {
    return "";
  }
};

/**
 * Creates query sting from selected filters
 * @param selections
 * @returns {string}
 */
export const createSelectionQuery = (selections: Selections) => {
  const { time_type, period_type, cloud_search_type, ...rest } = selections;
  const restSelectionsQuery = Object.entries(rest || {})
    .filter(([, value]) => value?.value)
    .map(
      ([key, value]) =>
        `${key}:${
          key === SUBMISSION_TIME
            ? stringToTimestamp(value?.value)
            : value?.value
        }`
    )
    .join(" AND ");
  const timeSelectionQuery = time_type
    ? `${time_type?.value}:${stringToTimestamp(period_type?.value)}`
    : "";

  if (!timeSelectionQuery) return restSelectionsQuery;
  if (!restSelectionsQuery) return timeSelectionQuery;
  return `${timeSelectionQuery} AND ${restSelectionsQuery}`;
};
/**
 * determines availability keyword based on cloudSearchType
 *
 * @param cloudSearchType
 * @returns {string}
 */
export const determineKeyword = (cloudSearchType = "") => {
  if (cloudSearchType.toLowerCase() === "private") return "available:false";
  if (cloudSearchType.toLowerCase() === "public") return "available:true";
  return "";
};

/**
 * Creates query string for API from selected filters and input field
 */
export const generateQueryWithSelections = (
  selections: Selections | undefined,
  q: string | undefined
): string => {
  const { cloud_search_type: { value = "" } = {} } = selections || {};
  const selectionQuery = createSelectionQuery(selections || {});
  const availabilityKeyword = ["private", "public"].includes(value)
    ? `AND ${determineKeyword(value)}`
    : "";
  if (!q && !selectionQuery) return determineKeyword(value);
  if (!q) return `${selectionQuery.trim()} ${availabilityKeyword}`.trim();
  if (!selectionQuery) return `${q.trim()} ${availabilityKeyword}`.trim();
  return `(${q}) AND ${selectionQuery} ${availabilityKeyword}`.trim();
};

export const removeExtraSpaces = (value: string) => {
  let modified = value.replace(/:(\s*)/g, ":");
  modified = modified.replace(/(\s*),(\s*)/g, ",");
  return modified;
};

export const getDisabledSelections = (query = "") =>
  [
    CLASSIFICATION,
    CLASS,
    AVAILABLE,
    SHAREABLE,
    IN_ALIAS,
    SUBMISSION_USER,
    PROCESSING_STATUS
  ]
    .filter(keyword => {
      const regex = new RegExp(`(^|\\s|[(])${keyword}`, "i");
      return regex.test(query);
    })
    .map(keyword => SEARCH_FIELD_TO_FILTER_MAP[keyword]);

export const mapSortFieldName = (fieldName: string, view = LOCATION.LOCAL) => {
  if (view === LOCATION.LOCAL && fieldName === "sha1") {
    return "filename";
  }
  if (
    view === LOCATION.CLOUD &&
    ["submission_time", "submission_user", "_available"].includes(fieldName)
  ) {
    return "firstseen";
  }
  return sortFieldMap[fieldName as keyof typeof sortFieldMap] || fieldName;
};

export const transformGridData = (
  data: CloudSearchSample[] | LocalSearchSample[]
) =>
  [...data].map(item =>
    mapKeys(
      item,
      (value, key) => gridDataFieldMap[key as GridDataFieldMapKeyType] || key
    )
  ) as unknown as CloudSample[] | LocalSample[];

export const createDropdownMenuItems = (
  items: string[] = [],
  allItemLabel = "All"
) => [
  {
    label: allItemLabel,
    value: ""
  },
  ...items.filter(Boolean).map((item: string) => ({
    label: item,
    value: item.toLowerCase()
  }))
];

export const createDropdownMenuItemsForUserList = (
  items: UserSampleCount[] = [],
  currentUsername = "",
  allItemLabel = "All"
) => [
  {
    label: allItemLabel,
    value: ""
  },
  {
    label: `${currentUsername} (Current User)`,
    value: currentUsername.toLowerCase(),
    count: items.find(({ username }) => username === currentUsername)?.count
  },
  ...items
    .filter(({ username }) => username != null && username !== currentUsername)
    .sort((a, b) => a.username.localeCompare(b.username))
    .map((item: UserSampleCount) => ({
      label: item.username,
      value: item.username.toLowerCase(),
      count: item.count
    }))
];

export const removeLabelFromSearchSelections = (selections: Selections = {}) =>
  Object.entries(selections).reduce(
    (curr, [key, { value }]) => ({
      ...curr,
      [key]: value
    }),
    {}
  );

export const prepareQueryAndSelectionsQueryParams = (
  q: string,
  sel: Selections
) => {
  const selections = Object.entries(sel).reduce(
    (curr, [key, value]) => ({
      ...curr,
      [key.replace("-", "_")]: value
    }),
    {}
  );
  if (!q) {
    return selections;
  }
  if (isEmpty(selections)) {
    return {
      query: q
    };
  }
  return {
    ...selections,
    query: q
  };
};

/**
 * Checks if it is basic search (search where query doesn't contain a keyword
 * Returns false if query starts with one of the SEARCH_FIELDS or has multiple values
 */
export const isBasicSearch = (searchQuery = "") => {
  if (!searchQuery) return false;
  const keywords = Object.values(SEARCH_FIELDS);
  if (searchQuery.split(/\s+/).length > 1) {
    return false;
  }
  if (searchQuery.startsWith("(")) {
    return !keywords.some(keyword =>
      searchQuery
        ?.substring(1)
        .trim()
        .toLowerCase()
        .startsWith(keyword?.trim().toLowerCase())
    );
  }
  return !keywords.some(keyword =>
    searchQuery?.trim().toLowerCase().startsWith(keyword?.trim().toLowerCase())
  );
};

export const getNtiValue = (query: string): string =>
  removeBrackets(query).match(NTI_KEYWORD_VALUE_REGEX)?.at(0) ?? "";

export const getIsBasicNtiSearch = (
  searchQuery = "",
  transformedQuery = ""
) => {
  if (!isBasicSearch(searchQuery?.trim())) return false;
  if (NTI_KEYWORDS.some(keyword => searchQuery.startsWith(keyword)))
    return false;

  const ntiValue = getNtiValue(transformedQuery);
  const [, value] = ntiValue.split(FIRST_COLON_ONLY_REGEX);

  if (!value) return false;

  return isNtiValue(removeSurroundingQuotes(value));
};

export const getIsSingleHashSearch = (
  requestedHashes: RequestedHashes | null
) => {
  if (!requestedHashes) return false;
  if (requestedHashes.invalid_hashes.length) return false;
  return requestedHashes.valid_hashes.length === 1;
};

/**
 * Returns basic search keyword from query
 * Basic search keywords are: hashes, ipv4, ipv6, uri and domain
 *
 * @param query
 * @returns {string}
 */
export const getBasicSearchKeyword = (query = ""): string | undefined => {
  const { HASHES, IPV4, IPV6, URI, DOMAIN, SHA1, MD5, SHA256 } = SEARCH_FIELDS;
  const potentialKeywords: Array<string> =
    query.split(" ").map(item => {
      const [keyword] = item.split(":");
      return keyword;
    }) || [];
  const keywords = Object.values([
    HASHES,
    IPV4,
    IPV6,
    URI,
    DOMAIN,
    SHA1,
    SHA256,
    MD5
  ]);
  const containedKeywords = potentialKeywords.filter((keyword: string) =>
    keywords.includes(`${keyword}:`)
  );
  if (containedKeywords.length === 1) {
    return containedKeywords.pop();
  }
  return undefined;
};

/**
 *
 * @param value
 * @returns {boolean}
 *
 * Checks if sent value is one of the time range keywords
 */
export const isTimeRangeKeyword = (value: TimePeriodEnumValue) =>
  Object.values(TimeRangeEnum).includes(value);

export const determineSelectionsFromQP = (qpSelection: Selections) =>
  Object.entries(qpSelection).reduce((curr, [key, val]) => {
    if ([SUBMISSION_TIME, FIRST_SEEN, LAST_SEEN].includes(key)) {
      return {
        ...curr,
        [TIME_TYPE]: key,
        [PERIOD_TYPE]: val
      };
    }
    return {
      ...curr,
      [key]: {
        label:
          !Object.values(DATE_FILTER).includes(val) && key === PERIOD_TYPE
            ? DATE_FILTER.CUSTOM
            : val,
        value: val
      }
    };
  }, {});

const isString = (error: unknown): error is string => typeof error === "string";
const isValidationError = (
  error: unknown
): error is AxiosResponse<IValidationError> =>
  !!(error as AxiosResponse<IValidationError>)?.data?.detail?.fields;

export const createErrorMessage = (
  error?: ErrorResponse<MessageErrorResponse> | string | null
) => {
  if (!error) return "";
  if (isString(error)) return error;

  const { status, data } = error || {};

  if (status === 502) {
    return "Error while fetching data. Please check the length of the query string as a possible cause.";
  }
  if (status === 429) {
    return "The daily/monthly quota has been reached, or the API rate limit exceeded. Please check your quota and try again later.";
  }
  if (status === 400 && isValidationError(error)) {
    return `Errors found in request parameters. ${Object.entries(
      error?.data?.detail.fields
    ).map(
      ([fieldName, errorList]) => `${fieldName}: ${errorList?.join(",")}`
    )}`;
  }

  if (!data) return "Failed to load data. Please try again.";
  if (isString(data)) return data;

  if (data.message) return data.message;

  return data;
};

export const constructQuery = (query: string, availability: string) => {
  if (!query) {
    return `${availability ?? ""}`;
  }
  if (availability) {
    return `(${query}) ${availability}`;
  }
  return query;
};

export const determineSort = (
  location: string,
  sort = { field: "", direction: "" }
) => `${mapSortFieldName(sort.field, location)} ${sort.direction}`;

export const constructSelections = (
  selections: Selections = {},
  keyCallback = (value: string) => value
) =>
  Object.entries(selections).reduce(
    (obj, [key, value]) => ({
      ...obj,
      [keyCallback(key)]: value.value || value
    }),
    {}
  );

export const showNoAccessNotification = () => {
  // don't keep showing multiple notifications on each keystroke
  if (!areNotificationsShown()) {
    show({
      level: NotificationsLevel.INFO,
      sticky: true,
      html: true,
      message: `You do not have access to this feature. Please contact
        <a href="mailto:insidesales@reversinglabs.com">ReversingLabs Sales Support</a>
        for more information.`
    });
  }
};

export const showCloudUnreachableNotification = () => {
  if (!areNotificationsShown()) {
    show({
      level: NotificationsLevel.INFO,
      sticky: true,
      html: true,
      message: `An error occurred communicating with Spectra Intelligence. 
                Spectra Intelligence may be unreachable from this machine or 
                Spectra Intelligence settings may not be properly configured.`
    });
  }
};

export const showCloudUnreachableOrNoAccessNotifications = (
  hasCloudSearchAccess: boolean | null
) => {
  if (hasCloudSearchAccess === false) {
    console.warn("Trying to fetch data with no rights!");
    showNoAccessNotification();
  }

  if (hasCloudSearchAccess === null) {
    console.warn(
      "Trying to fetch data but Spectra Intelligence is unreachable!"
    );
    showCloudUnreachableNotification();
  }
};

export const getIsoStartEndDateFromCustom = (customRange: string) => {
  const withoutFirstAndLast = customRange.slice(1, -1);
  return withoutFirstAndLast.split(/\s*TO\s*/i);
};

export const getStartEndDateFromCustom = (customRange: string) => {
  const [startIsoDate, endIsoDate] = getIsoStartEndDateFromCustom(customRange);
  const [startDateWithoutTime] = startIsoDate?.split("T");
  const [endDateWithoutTime] = endIsoDate?.split("T");
  return [startDateWithoutTime, endDateWithoutTime];
};

export const getSelectedPeriodTimerange = (period: Selection) => {
  const { value = "", label } = period || {};
  if (label === "custom") {
    return getIsoStartEndDateFromCustom(value).map(date =>
      date.replace(/[^\d:-]/g, " ").trim()
    );
  }
  const start = stringToTimestampStart(value, ISO_YEAR_MONTH_DAY_FORMAT_UTC);
  const end = moment().utc().format(ISO_YEAR_MONTH_DAY_FORMAT_UTC);
  return [start, end];
};

/**
 * @param query string
 * @param selections Selections
 * @param location string Cloud | Local
 *
 * Check if it is local search.
 */
export const shouldSwitchToLocal = (
  query: string,
  selections: Selections,
  location: string
) =>
  !query &&
  selections.classification?.value &&
  selections.time_type?.value !== FIRST_SEEN &&
  location === LOCATION.CLOUD;

export const tryCatch = async (tryFn: Promise<unknown>) => {
  try {
    const result = await tryFn;
    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

/**
 * Creates selections when filter is changed. If cloud is selected, it clears local only selections
 */
export const createFilterChangeSelections = (
  location: string,
  selections: Selections = {},
  disabledSelections: string[] = []
) => {
  if (location === LOCATION.CLOUD) {
    const selectionsWithoutLocalOnly = pickBy(
      selections,
      (_, key) =>
        [CLASSIFICATION, PERIOD_TYPE, CLOUD_SEARCH_TYPE].includes(key) &&
        !disabledSelections.includes(key)
    );

    return {
      ...DEFAULT_CLOUD_TIME_SELECTION,
      ...selectionsWithoutLocalOnly,
      [PERIOD_TYPE]:
        selectionsWithoutLocalOnly.period_type === undefined ||
        selectionsWithoutLocalOnly.period_type?.label === TimeEnum.ALL ||
        selectionsWithoutLocalOnly.period_type?.value?.includes("*")
          ? {
              value: TimeEnum.LAST_MONTH,
              label: TimeEnum.LAST_MONTH
            }
          : selectionsWithoutLocalOnly.period_type,
      [TIME_TYPE]: {
        value: FIRST_SEEN,
        label: FIRST_SEEN
      }
    };
  } else if (location === LOCATION.LOCAL) {
    const selectionsWithoutCloudOnly = pickBy(
      selections,
      (_, key) =>
        [
          CLASSIFICATION,
          PERIOD_TYPE,
          TIME_TYPE,
          SUBMISSION_USER,
          PROCESSING_STATUS,
          SAMPLE_ORIGIN
        ].includes(key) && !disabledSelections.includes(key)
    );
    return {
      ...DEFAULT_LOCAL_TIME_SELECTIONS,
      ...selectionsWithoutCloudOnly
    };
  } else if (location === LOCATION.NETWORK) {
    const filteredSelections = pick(selections, [
      "classification",
      "period_type",
      "time_type",
      "network_resource"
    ]);
    return {
      ...DEFAULT_NETWORK_TIME_SELECTION,
      ...filteredSelections
    };
  }

  return selections;
};

export const wrapInQuotes = (query: string) =>
  getLabelFromQuery(parseQuery(query));

/**
 * Returns grid availability icon
 * Local priority - first Top Container, second Url, third Extracted file
 * and last Dropped file
 * e.g. file that is both extracted and dropped should have extracted file icon
 * file that is both top container and extracted should have top container icon
 */
export const getAvailabilityIconAndTitle = (
  isCloud: boolean,
  isDownloadable: boolean,
  exceedsMaxFileSize: boolean,
  analyzer?: number,
  isExtracted?: boolean
): {
  icon: string;
  title: string;
} => {
  if (isCloud) {
    if (isDownloadable && !exceedsMaxFileSize) {
      return {
        icon: SampleAvailabilityIcons.CLOUD,
        title: SampleAvailabilityMessages.CLOUD
      };
    } else if (exceedsMaxFileSize) {
      return {
        icon: SampleAvailabilityIcons.MAXIMUM,
        title: SampleAvailabilityMessages.MAXIMUM
      };
    } else {
      return {
        icon: SampleAvailabilityIcons.CLOUD_UNAVAILABLE,
        title: SampleAvailabilityMessages.CLOUD_UNAVAILABLE
      };
    }
  }

  if (analyzer === TISCALE) {
    return {
      icon: SampleAvailabilityIcons.LOCAL_TOP_LEVEL,
      title: SampleAvailabilityMessages.LOCAL_TOP_LEVEL
    };
  }

  if (analyzer === URLANALYZER) {
    return {
      icon: SampleAvailabilityIcons.URL,
      title: SampleAvailabilityMessages.URL
    };
  }

  if (isExtracted) {
    return {
      icon: SampleAvailabilityIcons.LOCAL_EXTRACTED,
      title: SampleAvailabilityMessages.LOCAL_EXTRACTED
    };
  }

  if (analyzer === TISCALE_DROPPED_FILE) {
    return {
      icon: SampleAvailabilityIcons.DROPPED_FILE,
      title: SampleAvailabilityMessages.DROPPED_FILE
    };
  }

  return {
    icon: SampleAvailabilityIcons.LOCAL_TOP_LEVEL,
    title: SampleAvailabilityMessages.LOCAL_TOP_LEVEL
  };
};

export const isCloudDateLimitHit = (date?: string | null) => {
  if (!date) return false;
  return moment(date).isSameOrBefore(CLOUD_LIMIT_MOMENT);
};

const removeQuotes = (value: string) => {
  if (value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1).trim();
  }
  return value;
};

const filterTooLongNetworkValues = (values: string[]) => {
  const networkValues = values.filter(
    value => value.length <= MAX_NETWORK_VALUE_LENGTH
  );
  const tooLongNetworkValues = values.filter(
    value => value.length > MAX_NETWORK_VALUE_LENGTH
  );

  return { networkValues, tooLongNetworkValues };
};

export const extractNetworkValuesFromQuery = (query: string) => {
  const pQuery = parseQuery(query);

  return filterTooLongNetworkValues(
    pQuery.flatMap(({ keyword, grouped }) => {
      if (!keyword) return [];

      if (NTI_KEYWORDS.includes(keyword)) {
        if (grouped?.length === 2) {
          return removeQuotes(grouped[1].value);
        } else if ((grouped?.length ?? 0) > 2)
          return (
            grouped?.slice(2, -1).map(({ value }) => removeQuotes(value)) ?? []
          );
      }

      return [];
    })
  );
};

export const constructNetworkSelections = (selections?: Selections) =>
  constructSelections(
    {
      ...selections,
      period_type:
        selections?.time_type?.value === SUBMISSION_TIME
          ? selections.period_type
          : {
              label: DATE_FILTER.ALL,
              value: DATE_FILTER.ALL
            }
    },
    snakeCase
  );

export const getNonNTIKeywordsFromQuery = (query: string) =>
  Array.from(query.matchAll(NON_NETWORK_TAB_KEYWORD_VALUE_REGEX))
    ?.map(match => match?.[2]?.slice(0, -1))
    .filter(Boolean);
